import { gsap } from "gsap";
import { hydrateLazyVideo } from "./lazy-video.js";

// Video Card lightbox (bskr/video-card). Global partial — the card nests inside
// layouts (bskr/layout-text-video), where per-block scripts aren't reliably
// enqueued. Adapted from ividona-new: same GSAP thumbnail→fullscreen morph +
// volume fade, but the lightbox chrome is rendered in PHP, and it now stops/
// starts Lenis, closes on Escape, and respects reduced motion.

function start() {
  if (typeof window !== "undefined" && window.wp && window.wp.data) return;
  init();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}

function init() {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  document.querySelectorAll(".video-card").forEach((block) => {
    if (block.dataset.videoCardInited === "1") return;
    block.dataset.videoCardInited = "1";

    const trigger = block.querySelector("[data-video-player]");
    const videoWrapper = block.querySelector(".wp-block-video");
    const lightbox = block.querySelector("[data-video-lightbox]");
    if (!trigger || !videoWrapper || !lightbox) return;

    const sourceVideo = videoWrapper.querySelector("video");
    if (!sourceVideo) return;

    const wrapper = lightbox.querySelector("[data-video-lightbox-wrapper]");
    const closers = lightbox.querySelectorAll("[data-video-lightbox-close]");

    let isOpen = false;
    let onKeydown = null;

    const triggerRect = () => videoWrapper.getBoundingClientRect();
    const bodyRect = () =>
      lightbox.querySelector(".video-card__lightbox__body").getBoundingClientRect();

    const open = () => {
      if (isOpen) return;
      isOpen = true;

      // Clone the poster <video> into the lightbox and switch it to a real,
      // controllable player.
      wrapper.innerHTML = sourceVideo.outerHTML;
      const video = wrapper.querySelector("video");
      // The card's video is lazy (core/video render_block filter), so the clone
      // may arrive without a source: give it one before asking it to play.
      hydrateLazyVideo(video);
      video.removeAttribute("autoplay");
      video.controls = true;
      video.loop = true;
      video.playsInline = true;
      video.muted = false;
      video.volume = 0;

      lightbox.hidden = false;
      // Force reflow so the opening transition/measurement is reliable.
      void lightbox.offsetWidth;
      lightbox.classList.add("is-open");
      document.body.classList.add("is-lightbox-open");
      window.lenisInstance?.stop?.();

      const playWithFallback = () =>
        video.play().catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });

      if (reduce) {
        // No morph — just reveal and play.
        playWithFallback();
        gsap.to(video, { volume: 1, duration: 0.4, ease: "power2.out" });
        return;
      }

      playWithFallback();
      gsap.to(video, { volume: 1, duration: 0.8, delay: 0.3, ease: "power2.out" });

      gsap.fromTo(
        wrapper,
        {
          position: "fixed",
          top: `${triggerRect().y}px`,
          left: `${triggerRect().x}px`,
          width: `${triggerRect().width}px`,
          maxHeight: `${triggerRect().height}px`,
          borderRadius: window.getComputedStyle(videoWrapper).borderRadius,
        },
        {
          top: `${bodyRect().y}px`,
          left: `${bodyRect().x}px`,
          width: `${bodyRect().width}px`,
          maxHeight: `${bodyRect().height}px`,
          aspectRatio: "16 / 9",
          borderRadius: 0,
          duration: 0.8,
          delay: 0.3,
          ease: "expo.inOut",
          onComplete: () => gsap.set(wrapper, { clearProps: "all" }),
        }
      );
    };

    const close = () => {
      if (!isOpen) return;
      isOpen = false;

      const video = wrapper.querySelector("video");

      const finish = () => {
        if (video) video.pause();
        wrapper.innerHTML = "";
        lightbox.classList.remove("is-open");
        lightbox.hidden = true;
        document.body.classList.remove("is-lightbox-open");
        window.lenisInstance?.start?.();
      };

      if (video) gsap.to(video, { volume: 0, duration: 0.4, ease: "power2.in" });

      if (reduce || !video) {
        finish();
        return;
      }

      gsap.fromTo(
        wrapper,
        {
          position: "fixed",
          top: `${bodyRect().y}px`,
          left: `${bodyRect().x}px`,
          width: `${bodyRect().width}px`,
          maxHeight: `${bodyRect().height}px`,
          aspectRatio: "16 / 9",
        },
        {
          top: `${triggerRect().y}px`,
          left: `${triggerRect().x}px`,
          width: `${triggerRect().width}px`,
          maxHeight: `${triggerRect().height}px`,
          duration: 0.5,
          ease: "power2.inOut",
          onStart: () => {
            video.controls = false;
            video.loop = false;
          },
          onComplete: finish,
        }
      );
    };

    trigger.addEventListener("click", open);
    closers.forEach((el) => el.addEventListener("click", close));

    onKeydown = (e) => {
      if (e.key === "Escape" && isOpen) close();
    };
    document.addEventListener("keydown", onKeydown);
  });
}
